import React, { useState } from 'react'
import { X, FileText, Database } from 'lucide-react'
import {
  projectsApi,
  ParseAssessmentResultLegacy,
  LegacyRaRowScored,
} from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { vaiReplaceSummary } from '@/lib/vaiSummary'
import { Button } from '@/components/ui/Button'

interface Props {
  projectSystemId: string
  parseResult: ParseAssessmentResultLegacy
  onClose: () => void
  onCommitted: () => void
}

export const ImportLegacyRaModal: React.FC<Props> = ({
  projectSystemId,
  parseResult,
  onClose,
  onCommitted,
}) => {
  const { error: showError, success } = useToast()
  // default-pick row 0 (highest score after server-side sort)
  const [selectedIdx, setSelectedIdx] = useState<number>(parseResult.scored_rows.length > 0 ? 0 : -1)
  const [committing, setCommitting] = useState(false)

  const selected: LegacyRaRowScored | null =
    selectedIdx >= 0 ? parseResult.scored_rows[selectedIdx] : null

  const handleCommit = async () => {
    if (!selected) return
    setCommitting(true)
    try {
      // strip score before sending, server only wants LegacyRaRow shape
      const { score: _score, ...row } = selected
      const result = await projectsApi.commitLegacyAssessment(projectSystemId, {
        header: parseResult.header,
        row,
        source_filename: parseResult.source_filename,
        vai_rows:          parseResult.vai_preview?.rows,
        vai_vessel_header: parseResult.vai_preview?.vessel_header,
      })
      const vd = result.vai_deleted  ?? 0
      const vi = result.vai_inserted ?? 0
      const vs = result.vai_skipped  ?? 0
      if (vd > 0 || vi > 0 || vs > 0) {
        success(`Legacy RA imported. Vessel Asset Inventory: ${vaiReplaceSummary(vd, vi, vs)}.`)
      } else {
        success('Legacy risk assessment imported')
      }
      onCommitted()
    } catch (err: any) {
      showError(err.message ?? 'Failed to import legacy assessment')
    } finally {
      setCommitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-base font-semibold">Import Legacy Risk Assessment</h2>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              {parseResult.scored_rows.length} system{parseResult.scored_rows.length !== 1 ? 's' : ''} found in {parseResult.source_filename}. Pick the one that applies to this system.
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Header summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SummaryItem label="Company" value={parseResult.header.company_name} />
            <SummaryItem label="Target System" value={parseResult.header.target_system} />
            <SummaryItem label="Vendor PIC" value={parseResult.header.vendor_pic_name} />
          </div>

          {/* VAI sheet 3 preview / warning */}
          {parseResult.vai_preview && parseResult.vai_preview.row_count > 0 && (
            <div className="p-3 border border-sky-200 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-sky-600" />
                <span className="text-sm font-medium text-sky-800 dark:text-sky-300">
                  Vessel Asset Inventory detected
                </span>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                {parseResult.vai_preview.row_count} inventory row{parseResult.vai_preview.row_count !== 1 ? 's' : ''} from sheet 3 will be added to this system on import. Rows that match an existing
                (system, item number) pair are skipped automatically.
              </div>
            </div>
          )}
          {parseResult.vai_parse_error && (
            <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-500 dark:text-slate-400">
              Sheet 3 (Vessel Asset Inventory) not imported: {parseResult.vai_parse_error}
            </div>
          )}
          {/* QA item 1 (2026-06-12): sheet-found-but-zero-rows was the
              one SILENT path (banner above gates on row_count > 0, no
              parse error set), "nothing lands in VAI" with no
              explanation. now self-describes. */}
          {parseResult.vai_preview && parseResult.vai_preview.row_count === 0 && !parseResult.vai_parse_error && (
            <div className="p-3 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300">
              A Vessel Asset Inventory sheet was found, but 0 importable rows were extracted
              (template example rows and empty rows are skipped). Nothing will be added to the inventory.
            </div>
          )}

          {/* Row picker */}
          {parseResult.scored_rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400 border border-slate-200 dark:border-slate-700 rounded-lg">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No systems found in this workbook.
            </div>
          ) : (
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-900/40 sticky top-0">
                    <tr className="text-left text-[10px] text-slate-400 uppercase tracking-wide">
                      <th className="px-2 py-2 w-8"></th>
                      <th className="px-2 py-2">Function</th>
                      <th className="px-2 py-2">System</th>
                      <th className="px-2 py-2">Vendor</th>
                      <th className="px-2 py-2 w-12">Cat</th>
                      <th className="px-2 py-2 w-16">TA</th>
                      <th className="px-2 py-2">Risk Level</th>
                      <th className="px-2 py-2 w-16 text-right">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                    {parseResult.scored_rows.map((row, i) => {
                      const checked = selectedIdx === i
                      const goodScore = row.score >= 3
                      return (
                        <tr
                          key={i}
                          onClick={() => setSelectedIdx(i)}
                          className={`cursor-pointer ${
                            checked ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/40'
                          }`}
                        >
                          <td className="px-2 py-2 align-top">
                            <input
                              type="radio"
                              name="legacy-row"
                              checked={checked}
                              onChange={() => setSelectedIdx(i)}
                              className="text-blue-500"
                            />
                          </td>
                          <td className="px-2 py-2 text-slate-700 dark:text-slate-200">{row.ra_function ?? '—'}</td>
                          <td className="px-2 py-2 font-medium text-slate-900 dark:text-white">{row.ra_function_of_system ?? '—'}</td>
                          <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{row.ra_vendor ?? '—'}</td>
                          <td className="px-2 py-2 text-slate-500">{row.cbs_interpretation_2 ?? '—'}</td>
                          <td className="px-2 py-2 text-slate-500">{row.type_approval_status ?? '—'}</td>
                          <td className="px-2 py-2 text-slate-500">{row.iacs_risk_level ?? '—'}</td>
                          <td className="px-2 py-2 text-right">
                            <span
                              className={
                                'inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums ' +
                                (goodScore
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400')
                              }
                            >
                              {row.score}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <div className="text-[10px] text-slate-400">
            {selected
              ? `Selected: ${selected.ra_function_of_system ?? selected.ra_function ?? 'row ' + (selectedIdx + 1)}`
              : 'No row selected'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={committing}>
              Cancel
            </Button>
            <Button onClick={handleCommit} disabled={!selected || committing} size="sm">
              {committing ? 'Importing…' : 'Import'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

const SummaryItem: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 bg-slate-50 dark:bg-slate-900/40">
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate mt-0.5">
      {value ?? <span className="text-slate-300 dark:text-slate-600 italic">—</span>}
    </div>
  </div>
)
